import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileSpreadsheet, Upload, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Org library — UI skeleton modelled on
 * /ai-generated-sofs/rcm-org-library-mock.html. Three cards (process
 * catalog, risk register, control library) covering the master data
 * needed before a Strict RCM import will accept references.
 *
 * Risk register links to the existing /risks list page; control library
 * links to /controls. Process catalog + the bulk-import buttons toast
 * "Coming soon" — those are downstream of an importer that doesn't
 * exist yet.
 */
function RcmOrgLibrary() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-xs text-gray-500 mb-3">
          SOX / Settings /{' '}
          <Link className="text-teal-700 hover:underline" to="/risk-control-matrix/import">RCM import</Link>{' '}
          / Org library
        </div>

        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Shield className="h-6 w-6 text-teal-600 shrink-0" aria-hidden />
          Org library
          <span className="text-sm font-medium text-gray-400 ml-2">(master data)</span>
        </h1>
        <p className="text-sm text-gray-600 mt-1 max-w-2xl">
          For <strong>strict</strong> RCM import, <span className="font-mono text-blue-700">sox_process_key</span>,
          {' '}<span className="font-mono text-blue-700">risk_ref</span>, and
          {' '}<span className="font-mono text-blue-700">control_ref</span> in the file must match what
          already lives here. Use the bulk tools before — or instead of — ad-hoc resolution on
          every import.
        </p>

        <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900 leading-relaxed">
          <strong>When &ldquo;everything must exist first&rdquo; makes sense:</strong> large
          programs with a stable control library and risk register; the RCM file is{' '}
          <em>linkage and narrative</em> only, not a back door for creating net-new production
          records without workflow.
        </div>

        <div className="mt-5 space-y-3">
          <LibraryCard
            title="Process & SOX cycle catalog"
            hint="Keys used in RCM: OTC, P2P, R2R, or custom (e.g. OTC-RETURNS)"
            actions={(
              <>
                <ActionButton onClick={() => toast('Catalog template download coming soon', { icon: 'ℹ️' })} icon={FileSpreadsheet}>
                  Download CSV template
                </ActionButton>
                <ActionButton onClick={() => toast('Catalog upload coming soon', { icon: 'ℹ️' })} icon={Upload} primary>
                  Upload / replace
                </ActionButton>
              </>
            )}
          />

          <LibraryCard
            title="Risk register"
            hint="Ensure every RSK_* you will reference in RCM is created or bulk-imported here first."
            actions={(
              <>
                <Link to="/risks" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                  <ExternalLink className="h-3.5 w-3.5" /> Open risks
                </Link>
                <ActionButton onClick={() => toast('Bulk risk import coming soon', { icon: 'ℹ️' })} icon={Upload}>
                  Batch import
                </ActionButton>
              </>
            )}
          />

          <LibraryCard
            title="Control library"
            hint="Every CTL_* / IT-CTL_* in the RCM file should map to a control object (owner, evidence)."
            actions={(
              <>
                <Link to="/controls" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                  <ExternalLink className="h-3.5 w-3.5" /> Open controls
                </Link>
                <ActionButton onClick={() => toast('Bulk control import coming soon', { icon: 'ℹ️' })} icon={FileSpreadsheet}>
                  Download template
                </ActionButton>
                <ActionButton onClick={() => toast('Bulk control upload coming soon', { icon: 'ℹ️' })} icon={Upload} primary>
                  Upload
                </ActionButton>
              </>
            )}
          />
        </div>

        <p className="text-[11px] text-gray-400 mt-5 leading-relaxed">
          ITGC works the same way:{' '}
          <span className="font-mono text-blue-700">itgc_domain</span> and control IDs in the
          library must line up. Then{' '}
          <Link to="/risk-control-matrix/import" className="text-teal-700 hover:underline">RCM import</Link>{' '}
          is mostly validation + mapping, not data inventing.
        </p>
      </div>
    </div>
  );
}

function LibraryCard({ title, hint, actions }) {
  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4">
      <h2 className="text-sm font-bold text-gray-900">{title}</h2>
      <p className="text-xs text-gray-500 mt-1 mb-3">{hint}</p>
      <div className="flex flex-wrap gap-2">{actions}</div>
    </section>
  );
}

function ActionButton({ children, onClick, icon: Icon, primary }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${
        primary
          ? 'text-white bg-teal-600 hover:bg-teal-700 border border-teal-600'
          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
      }`}
    >
      {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
      {children}
    </button>
  );
}

export default RcmOrgLibrary;
